import { useCallback } from 'react';
import * as PIXI from 'pixi.js';
import { sound } from '@pixi/sound';
import { GifSprite } from 'pixi.js/gif';
import { useDrag } from './useDrag';
import { pixiVideoSprite } from '../utils/createPixiTexture';
import { useCanvas } from './useCanvas';
import { componentToCanvasAPI } from '../services/api';
import useCanvasStore from '../stores/canvasStore';
import { ensureFontLoaded } from '../utils/ensureFontLoaded';


const serverBaseURL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace("/api/v1", "")
  : "http://localhost:8000";

// Helper function to create a placeholder sprite when texture loading fails
const createPlaceholderSprite = (componentData, componentId, x, y, viewport, dragStart, setHoveredElement, canvasElements, errorMessage) => {
  console.warn('Creating placeholder sprite due to error:', errorMessage);

  // Create a graphics object as placeholder
  const placeholder = new PIXI.Graphics();

  // Draw a grey rectangle with error indicator
  const width = 400;
  const height = 300;

  // Grey background
  placeholder.rect(-width / 2, -height / 2, width, height);
  placeholder.fill(0x808080, 0.5);

  // Border
  placeholder.rect(-width / 2, -height / 2, width, height);
  placeholder.stroke({ width: 2, color: 0x666666 });

  // Draw an X to indicate error
  placeholder.moveTo(-width / 4, -height / 4);
  placeholder.lineTo(width / 4, height / 4);
  placeholder.moveTo(width / 4, -height / 4);
  placeholder.lineTo(-width / 4, height / 4);
  placeholder.stroke({ width: 3, color: 0x444444 });

  // Set position
  placeholder.x = x;
  placeholder.y = y;
  placeholder.eventMode = 'static';
  placeholder.cursor = 'default';

  // Scale to reasonable size
  const maxSize = viewport.worldHeight / 1.2;
  const scale = Math.min(maxSize / width, maxSize / height);
  placeholder.scale.set(scale);

  // Store references
  placeholder.componentData = componentData;
  placeholder.elementId = componentId || `component_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  placeholder.isPlaceholder = true; // Flag to identify placeholder sprites
  placeholder.loadError = errorMessage; // Store error message

  // Create invisible interactive area
  const interactiveArea = new PIXI.Graphics();
  interactiveArea.rect(-width / 2, -height / 2, width, height);
  interactiveArea.fill(0x000000, 0);
  interactiveArea.x = x;
  interactiveArea.y = y;
  interactiveArea.eventMode = 'static';
  interactiveArea.cursor = 'default';
  interactiveArea.scale.set(scale);
  interactiveArea.elementId = placeholder.elementId;

  placeholder.interactiveArea = interactiveArea;

  // Add drag functionality
  const handlePointerDown = (event) => {
    let targetSprite = placeholder;

    if (event.currentTarget === interactiveArea && interactiveArea.elementId) {
      const canvasElement = useCanvasStore.getState().canvasElements.find(el => el.id === interactiveArea.elementId);
      if (canvasElement && canvasElement.pixiSprite) {
        targetSprite = canvasElement.pixiSprite;
      }
    }

    if (!targetSprite || targetSprite.destroyed || targetSprite.scale === null) {
      console.warn('Invalid or destroyed sprite reference in handlePointerDown');
      return;
    }

    if (!targetSprite.parent) {
      console.warn('Sprite is no longer in the scene');
      return;
    }

    dragStart(targetSprite, event);
  };

  placeholder.on('pointerdown', handlePointerDown);
  interactiveArea.on('pointerdown', handlePointerDown);

  // Add hover handlers
  const handlePointerOver = () => {
    if (placeholder && placeholder.parent && placeholder.elementId) {
      setHoveredElement(placeholder.elementId);
    }
  };

  const handlePointerOut = () => {
    setHoveredElement(null);
  };

  placeholder.on('pointerover', handlePointerOver);
  placeholder.on('pointerout', handlePointerOut);
  interactiveArea.on('pointerover', handlePointerOver);
  interactiveArea.on('pointerout', handlePointerOut);

  return placeholder;
};

export const useSpriteCreators = (viewport, updateElement, selectElement, updateCurrentTransform, canvasElements, setHoveredElement, videoTextureManager = null) => {
  const { dragStart } = useDrag(viewport, updateElement, selectElement, updateCurrentTransform, () => canvasElements);

  // Create a draggable video sprite
  const createVideoSprite = useCallback(async (componentData, componentId, x = 0, y = 0, agComponentSrc) => {
    try {
      const elementId = componentId || `component_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      let videoUrl;

      // Check if this is an AG Component (has fields)
      if (componentData.fields && componentData.fields.length > 0) {

        // Recreating sprite (undo/redo) - use the stored src
        if (componentId && agComponentSrc) {
          videoUrl = agComponentSrc;
        }
        // First time adding - fetch from server and store the path
        else if (!componentId) {
          // Use currSrc if available (from template with existing render), otherwise use preview
          const sourceToUse = componentData.currSrc || componentData.preview;
          const sourceRelativePath = sourceToUse.replace(serverBaseURL, '');
          const result = await componentToCanvasAPI.addComponent(elementId, sourceRelativePath);
          videoUrl = result.data.lowResPath;

          // Store this URL in componentData for future use
          componentData.currSrc = videoUrl;
        }
        // Fallback - should not happen in normal flow
        else {
          console.warn('AG Component without src, using preview');
          videoUrl = componentData.preview;
        }
      }

      // Regular video/media - use url or preview
      else {
        videoUrl = componentData.url || componentData.preview;
      }

      // Create the sprite with the determined URL
      const sprite = await pixiVideoSprite(
        componentData,
        videoUrl,
        viewport,
        dragStart,
        setHoveredElement,
        canvasElements,
        elementId,
        x,
        y,
        videoTextureManager
      );

      return sprite;
    } catch (error) {
      console.error('Error creating video sprite, creating placeholder:', error);
      // Create placeholder sprite instead of failing
      return createPlaceholderSprite(
        componentData,
        componentId,
        x,
        y,
        viewport,
        dragStart,
        setHoveredElement,
        canvasElements,
        `Video load failed: ${error.message}`
      );
    }
  }, [dragStart, canvasElements, viewport, setHoveredElement, videoTextureManager]);

  // Create a draggable image sprite
  const createImageSprite = useCallback(async (componentData, componentId, x = 0, y = 0) => {
    try {
      const imageUrl = componentData.preview || componentData.url;

      // Check if URL is accessible before attempting to load (quick 404 check)
      try {
        const response = await fetch(imageUrl, { method: 'HEAD' });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (fetchError) {
        console.error('Image URL not accessible:', fetchError);
        // Create placeholder immediately for 404 or network errors
        return createPlaceholderSprite(
          componentData,
          componentId,
          x,
          y,
          viewport,
          dragStart,
          setHoveredElement,
          canvasElements,
          `Image not accessible: ${fetchError.message}`
        );
      }

      // Load texture with timeout (30 seconds for large images on slow networks)
      const LOAD_TIMEOUT = 30000;
      const texture = await Promise.race([
        PIXI.Assets.load({
          src: imageUrl,
          loadParser: 'loadTextures', // Explicitly tell PixiJS to use the texture loader
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Image load timeout - file may be too large or network is slow')), LOAD_TIMEOUT)
        )
      ]);

      // Set alpha mode to prevent premultiplication artifacts
      if (texture.source) {
        texture.source.alphaMode = "no-premultiply-alpha";
      }

      const sprite = new PIXI.Sprite(texture);

      // Set sprite properties
      sprite.x = x;
      sprite.y = y;
      sprite.anchor.set(0.5);
      sprite.eventMode = 'static';
      sprite.cursor = 'default';

      // Calculate dimensions
      const spriteWidth = sprite.width;
      const spriteHeight = sprite.height;
      const spriteAnchorX = -spriteWidth / 2;
      const spriteAnchorY = -spriteHeight / 2;

      // Scale to reasonable size
      const maxSize = viewport.worldHeight / 1.2;
      const scale = Math.min(maxSize / spriteWidth, maxSize / spriteHeight);
      sprite.scale.set(scale);

      // Store references
      sprite.componentData = componentData;
      sprite.elementId = componentId || `component_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Create invisible interactive area for extended hit area
      const interactiveArea = new PIXI.Graphics();
      interactiveArea.rect(spriteAnchorX, spriteAnchorY, spriteWidth, spriteHeight);
      interactiveArea.fill(0x000000, 0); // Transparent
      interactiveArea.x = x;
      interactiveArea.y = y;
      interactiveArea.eventMode = 'static';
      interactiveArea.cursor = 'default';
      interactiveArea.scale.set(scale);
      interactiveArea.elementId = sprite.elementId;

      // Store reference to interactive area
      sprite.interactiveArea = interactiveArea;

      // Add drag and transform functionality
      const handlePointerDown = (event) => {
        let targetSprite = sprite;

        if (event.currentTarget === interactiveArea && interactiveArea.elementId) {
          const canvasElement = useCanvasStore.getState().canvasElements.find(el => el.id === interactiveArea.elementId);
          if (canvasElement && canvasElement.pixiSprite) {
            targetSprite = canvasElement.pixiSprite;
          }
        }

        if (!targetSprite || targetSprite.destroyed || targetSprite.scale === null) {
          console.warn('Invalid or destroyed sprite reference in handlePointerDown');
          return;
        }

        if (!targetSprite.parent) {
          console.warn('Sprite is no longer in the scene');
          return;
        }

        dragStart(targetSprite, event);
      };

      sprite.on('pointerdown', handlePointerDown);
      interactiveArea.on('pointerdown', handlePointerDown);

      // Add hover handlers
      const handlePointerOver = () => {
        if (sprite && sprite.parent && sprite.elementId) {
          setHoveredElement(sprite.elementId);
        }
      };

      const handlePointerOut = () => {
        setHoveredElement(null);
      };

      sprite.on('pointerover', handlePointerOver);
      sprite.on('pointerout', handlePointerOut);
      interactiveArea.on('pointerover', handlePointerOver);
      interactiveArea.on('pointerout', handlePointerOut);

      return sprite;
    } catch (error) {
      console.error('Error creating image sprite, creating placeholder:', error);
      // Create placeholder sprite instead of failing
      return createPlaceholderSprite(
        componentData,
        componentId,
        x,
        y,
        viewport,
        dragStart,
        setHoveredElement,
        canvasElements,
        `Image load failed: ${error.message}`
      );
    }
  }, [dragStart, canvasElements, viewport, setHoveredElement]);

  // Create an audio sprite (invisible sprite for timeline control only - no interaction)
  const createAudioSprite = useCallback(async (componentData, componentId, x = 0, y = 0) => {
    try {
      // Create an empty container sprite (no visual representation)
      const sprite = new PIXI.Sprite();

      // Set basic sprite properties (position doesn't matter since it's invisible)
      sprite.x = 0;
      sprite.y = 0;
      sprite.eventMode = 'none'; // No interaction needed

      // Set minimal dimensions (needed for the timeline)
      sprite.width = 1;
      sprite.height = 1;

      // Store references
      sprite.componentData = componentData;
      sprite.elementId = componentId || `component_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Generate unique sound ID for this audio instance
      const soundId = `audio_${sprite.elementId}`;

      try {
        console.log(`Loading audio from URL: ${componentData.url}`);

        // First, fetch the audio file as an ArrayBuffer
        const response = await fetch(componentData.url);
        const arrayBuffer = await response.arrayBuffer();

        // Wait for the sound to be fully loaded and decoded
        const soundInstance = await new Promise((resolve, reject) => {
          const instance = sound.add(soundId, {
            source: arrayBuffer,
            autoPlay: false,
            loop: false,
            volume: 0, // Start muted (will be controlled by element.muted state)
            singleInstance: true, // Only one instance can play at a time
            preload: true,
            loaded: (err, snd) => {
              if (err) {
                // If decoding fails, clean up the broken sound instance
                if (sound.exists(soundId)) {
                  sound.remove(soundId);
                }
                reject(err);
              } else {
                resolve(snd);
              }
            }
          });

          // Fallback resolve if it's already loaded synchronously (unlikely with arrayBuffers but good practice)
          if (instance && instance.isLoaded) {
            resolve(instance);
          }
        });

        if (soundInstance && soundInstance.media) {
          // Access the HTML Audio element from PIXI Sound
          const audioElement = soundInstance.media;
          // console.log(`Audio loaded successfully via PIXI Sound for ${soundId}`);

          // Store both the PIXI sound instance and the audio element
          sprite.audioElement = audioElement;
          sprite.soundId = soundId;
          sprite.pixiSound = soundInstance;

          // Store cleanup function
          sprite._cleanupAudio = () => {
            // Clear references first to prevent access to null objects
            sprite.soundId = null;
            sprite.pixiSound = null;
            sprite.audioElement = null;

            // Stop and remove the sound from PIXI Sound after clearing references
            // Use a timeout to ensure any pending callbacks complete
            setTimeout(() => {
              if (sound.exists(soundId)) {
                try {
                  sound.stop(soundId);
                  sound.remove(soundId);
                  // console.log(`Cleaned up PIXI sound ${soundId}`);
                } catch (e) {
                  console.warn(`Error cleaning up sound ${soundId}:`, e);
                }
              }
            }, 0);
          };
        } else {
          throw new Error('Failed to get sound instance or media element');
        }

      } catch (loadError) {
        console.error('Failed to load audio with PIXI Sound:', loadError);
        // Return sprite without sound if loading fails
        sprite.audioElement = null;
        sprite.soundId = null;
        sprite.pixiSound = null;
        sprite._cleanupAudio = () => { };
      }

      // No interactive area needed for audio
      sprite.interactiveArea = null;

      // No drag, scale, rotation, or hover handlers needed for audio

      return sprite;
    } catch (error) {
      console.error('Error creating audio sprite:', error);
      throw error;
    }
  }, []); // No dependencies needed since audio doesn't interact

  // Create a text sprite
  const createTextSprite = useCallback(async (componentData, componentId, x = 0, y = 0) => {
    try {
      // Preload font to ensure Canvas2D renders with the correct font
      const fontFamily = componentData.fontFamily || 'Arial';
      await ensureFontLoaded(fontFamily);

      const style = new PIXI.TextStyle({
        fontFamily,
        fontSize: componentData.fontSize || 36,
        fill: componentData.color || 0xffffff,
        align: componentData.align || 'center',
        wordWrap: componentData.wordWrap || false,
        wordWrapWidth: componentData.wordWrapWidth || 440,
      });

      const text = new PIXI.Text({
        text: componentData.text || 'Text',
        style,
        resolution: Math.max(2, window.devicePixelRatio || 2)
      });

      // Set sprite properties
      text.x = x;
      text.y = y;
      text.anchor.set(0.5);
      text.eventMode = 'static';
      text.cursor = 'default';

      // Store references
      text.componentData = componentData;
      text.elementId = componentId || `component_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Calculate dimensions
      const textWidth = text.width;
      const textHeight = text.height;

      // Create invisible interactive area
      const interactiveArea = new PIXI.Graphics();
      interactiveArea.rect(-textWidth / 2, -textHeight / 2, textWidth, textHeight);
      interactiveArea.fill(0x000000, 0);
      interactiveArea.x = x;
      interactiveArea.y = y;
      interactiveArea.eventMode = 'static';
      interactiveArea.cursor = 'default';
      interactiveArea.elementId = text.elementId;

      text.interactiveArea = interactiveArea;

      // Add drag and transform functionality
      const handlePointerDown = (event) => {
        let targetSprite = text;

        if (event.currentTarget === interactiveArea && interactiveArea.elementId) {
          const canvasElement = useCanvasStore.getState().canvasElements.find(el => el.id === interactiveArea.elementId);
          if (canvasElement && canvasElement.pixiSprite) {
            targetSprite = canvasElement.pixiSprite;
          }
        }

        if (!targetSprite || targetSprite.destroyed || targetSprite.scale === null) {
          console.warn('Invalid or destroyed sprite reference in handlePointerDown');
          return;
        }

        if (!targetSprite.parent) {
          console.warn('Sprite is no longer in the scene');
          return;
        }

        dragStart(targetSprite, event);
      };

      text.on('pointerdown', handlePointerDown);
      interactiveArea.on('pointerdown', handlePointerDown);

      // Add hover handlers
      const handlePointerOver = () => {
        if (text && text.parent && text.elementId) {
          setHoveredElement(text.elementId);
        }
      };

      const handlePointerOut = () => {
        setHoveredElement(null);
      };

      text.on('pointerover', handlePointerOver);
      text.on('pointerout', handlePointerOut);
      interactiveArea.on('pointerover', handlePointerOver);
      interactiveArea.on('pointerout', handlePointerOut);

      return text;
    } catch (error) {
      console.error('Error creating text sprite:', error);
      throw error;
    }
  }, [dragStart, canvasElements, viewport, setHoveredElement]);

  // Create a draggable GIF sprite
  const createGifSprite = useCallback(async (componentData, componentId, x = 0, y = 0) => {
    try {
      const gifUrl = componentData.preview || componentData.url;

      if (!gifUrl) {
        console.error('No GIF URL provided in componentData:', componentData);
        // Create placeholder for missing URL
        return createPlaceholderSprite(
          componentData,
          componentId,
          x,
          y,
          viewport,
          dragStart,
          setHoveredElement,
          canvasElements,
          'GIF URL not provided'
        );
      }

      // Check if URL is accessible before attempting to load (quick 404 check)
      try {
        const response = await fetch(gifUrl, { method: 'HEAD' });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (fetchError) {
        console.error('GIF URL not accessible:', fetchError);
        // Create placeholder immediately for 404 or network errors
        return createPlaceholderSprite(
          componentData,
          componentId,
          x,
          y,
          viewport,
          dragStart,
          setHoveredElement,
          canvasElements,
          `GIF not accessible: ${fetchError.message}`
        );
      }

      // Load GIF source with robust cache handling and timeout
      const LOAD_TIMEOUT = 30000; // 30 seconds for GIFs
      let source;
      let loadAttempts = 0;
      const maxAttempts = 2;

      while (loadAttempts < maxAttempts && !source) {
        try {
          loadAttempts++;

          // Check if asset exists in cache
          if (PIXI.Assets.cache.has(gifUrl)) {
            const cachedSource = PIXI.Assets.cache.get(gifUrl);

            // Validate cached source
            if (cachedSource && cachedSource.frames && Array.isArray(cachedSource.frames) && cachedSource.frames.length > 0) {
              source = cachedSource;
              console.log(`Using valid cached GIF source (attempt ${loadAttempts}):`, gifUrl);
            } else {
              // Cached source is invalid - unload and try fresh load
              console.warn(`Cached GIF source invalid (attempt ${loadAttempts}), reloading:`, gifUrl);
              try {
                await PIXI.Assets.unload(gifUrl);
              } catch (unloadError) {
                console.warn('Error unloading invalid GIF cache:', unloadError);
              }
              // Will retry in next iteration
            }
          } else {
            // Not in cache - load fresh with timeout
            console.log(`Loading fresh GIF source (attempt ${loadAttempts}):`, gifUrl);
            source = await Promise.race([
              PIXI.Assets.load(gifUrl),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('GIF load timeout - file may be too large or network is slow')), LOAD_TIMEOUT)
              )
            ]);
          }

          // Final validation before using
          if (source && (!source.frames || !Array.isArray(source.frames) || source.frames.length === 0)) {
            console.error(`Loaded GIF source is invalid (attempt ${loadAttempts}):`, source);
            source = null;
            // Unload for next attempt
            try {
              await PIXI.Assets.unload(gifUrl);
            } catch (e) {
              console.warn('Error unloading after validation failure:', e);
            }
          }
        } catch (loadError) {
          console.error(`Failed to load GIF source (attempt ${loadAttempts}):`, loadError);
          source = null;
          // Try to clean up for next attempt
          try {
            if (PIXI.Assets.cache.has(gifUrl)) {
              await PIXI.Assets.unload(gifUrl);
            }
          } catch (e) {
            console.warn('Error cleaning up after load failure:', e);
          }
        }
      }

      if (!source) {
        console.error(`GIF source is null after ${maxAttempts} attempts for URL:`, gifUrl);
        // Create placeholder for failed load
        return createPlaceholderSprite(
          componentData,
          componentId,
          x,
          y,
          viewport,
          dragStart,
          setHoveredElement,
          canvasElements,
          `GIF load failed after ${maxAttempts} attempts`
        );
      }

      // Create GIF sprite with manual update control
      const sprite = new GifSprite(source, {
        autoPlay: false,      // Don't auto-play, we'll control it via timeline
        loop: true,           // Loop the animation
        animationSpeed: 1,    // Normal speed
        autoUpdate: false,    // IMPORTANT: Manual updates for timeline sync
        currentFrame: 0,      // Start at first frame
      });

      // Wait for the GIF to be ready (textures loaded)
      await new Promise((resolve) => {
        if (sprite.totalFrames > 0) {
          resolve();
        } else {
          // Wait for onComplete or a short timeout
          const checkReady = setInterval(() => {
            if (sprite.totalFrames > 0) {
              clearInterval(checkReady);
              resolve();
            }
          }, 50);

          // Timeout after 2 seconds
          setTimeout(() => {
            clearInterval(checkReady);
            resolve();
          }, 2000);
        }
      });

      // Set sprite properties
      sprite.x = x;
      sprite.y = y;
      sprite.anchor.set(0.5);
      sprite.eventMode = 'static';
      sprite.cursor = 'default';

      // Calculate dimensions
      const spriteWidth = sprite.width;
      const spriteHeight = sprite.height;
      const spriteAnchorX = -spriteWidth / 2;
      const spriteAnchorY = -spriteHeight / 2;

      // Scale to reasonable size
      const maxSize = viewport.worldHeight / 1.2;
      const scale = Math.min(maxSize / spriteWidth, maxSize / spriteHeight);
      sprite.scale.set(scale);

      // Store references
      sprite.componentData = componentData;
      sprite.elementId = componentId || `component_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sprite.isGif = true; // Flag to identify GIF sprites

      // Create invisible interactive area for extended hit area
      const interactiveArea = new PIXI.Graphics();
      interactiveArea.rect(spriteAnchorX, spriteAnchorY, spriteWidth, spriteHeight);
      interactiveArea.fill(0x000000, 0); // Transparent
      interactiveArea.x = x;
      interactiveArea.y = y;
      interactiveArea.eventMode = 'static';
      interactiveArea.cursor = 'default';
      interactiveArea.scale.set(scale);
      interactiveArea.elementId = sprite.elementId;

      // Store reference to interactive area
      sprite.interactiveArea = interactiveArea;

      // Add drag and transform functionality
      const handlePointerDown = (event) => {
        let targetSprite = sprite;

        if (event.currentTarget === interactiveArea && interactiveArea.elementId) {
          const canvasElement = useCanvasStore.getState().canvasElements.find(el => el.id === interactiveArea.elementId);
          if (canvasElement && canvasElement.pixiSprite) {
            targetSprite = canvasElement.pixiSprite;
          }
        }

        if (!targetSprite || targetSprite.destroyed || targetSprite.scale === null) {
          console.warn('Invalid or destroyed sprite reference in handlePointerDown');
          return;
        }

        if (!targetSprite.parent) {
          console.warn('Sprite is no longer in the scene');
          return;
        }

        dragStart(targetSprite, event);
      };

      sprite.on('pointerdown', handlePointerDown);
      interactiveArea.on('pointerdown', handlePointerDown);

      // Add hover handlers
      const handlePointerOver = () => {
        if (sprite && sprite.parent && sprite.elementId) {
          setHoveredElement(sprite.elementId);
        }
      };

      const handlePointerOut = () => {
        setHoveredElement(null);
      };

      sprite.on('pointerover', handlePointerOver);
      sprite.on('pointerout', handlePointerOut);
      interactiveArea.on('pointerover', handlePointerOver);
      interactiveArea.on('pointerout', handlePointerOut);

      return sprite;
    } catch (error) {
      console.error('Error creating GIF sprite, creating placeholder:', error);
      // Create placeholder sprite instead of failing
      return createPlaceholderSprite(
        componentData,
        componentId,
        x,
        y,
        viewport,
        dragStart,
        setHoveredElement,
        canvasElements,
        `GIF load failed: ${error.message}`
      );
    }
  }, [dragStart, canvasElements, viewport, setHoveredElement]);

  // Create an AI sprite — if media was previously selected (url exists),
  // create the actual media sprite; otherwise show a placeholder.
  const createAiSprite = useCallback(async (componentData, componentId, x = 0, y = 0) => {
    try {
      // Use pre-generated spriteId from AiDialog if available, then componentId, then random
      const elementId = componentData.spriteId || componentId || `ai_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const model = componentData.aiPayload?.settings?.model || '';
      const isAudioOnly = model === 'sound-effects' || model === 'voiceover';

      // Audio-only AI models (e.g. sound-effects, voiceover) — no visual on canvas, same as regular audio sprite
      if (isAudioOnly) {
        const sprite = new PIXI.Sprite();
        sprite.x = 0;
        sprite.y = 0;
        sprite.eventMode = 'none';
        sprite.width = 1;
        sprite.height = 1;
        sprite.componentData = componentData;
        sprite.elementId = elementId;
        sprite.isAiSprite = true;
        sprite.interactiveArea = null;
        return sprite;
      }

      // If this AI sprite already has selected media (e.g. project reload),
      // create the actual media sprite instead of a placeholder.
      if (componentData.url && componentData.aiMediaType) {
        let sprite = null;
        const mediaType = componentData.aiMediaType;

        if (mediaType === 'image') {
          sprite = await createImageSprite(componentData, elementId, x, y);
        } else if (mediaType === 'video') {
          sprite = await createVideoSprite(componentData, elementId, x, y, null);
        } else if (mediaType === 'audio') {
          sprite = await createAudioSprite(componentData, elementId, x, y);
        } else if (mediaType === 'gif') {
          sprite = await createGifSprite(componentData, elementId, x, y);
        }

        if (sprite) {
          sprite.isAiSprite = true;
          sprite.componentData = componentData;
          return sprite;
        }
        // Fall through to placeholder if sprite creation fails
      }

      // Determine dimensions from the selected aspect ratio
      const aspectRatio = componentData.aiPayload?.settings?.aspectRatio || '16:9';
      const BASE = 400; // base dimension for the longer side
      let width, height;
      if (aspectRatio === '9:16') {
        width = BASE * (9 / 16);
        height = BASE;
      } else if (aspectRatio === '1:1') {
        width = BASE;
        height = BASE;
      } else {
        // Default 16:9
        width = BASE;
        height = BASE * (9 / 16);
      }

      // Create a container for the AI placeholder
      const placeholder = new PIXI.Container();

      // --- Background ---
      const bg = new PIXI.Graphics();
      bg.roundRect(-width / 2, -height / 2, width, height, 12);
      bg.fill(0x111111, 0.92);
      bg.roundRect(-width / 2, -height / 2, width, height, 12);
      bg.stroke({ width: 1.5, color: 0xc6e982, alpha: 0.18 });
      placeholder.addChild(bg);

      // --- Media type icon (image or video) ---
      const icon = new PIXI.Graphics();
      const mediaType = componentData.aiPayload?.settings?.model || '';
      const isVideo = mediaType.includes('video') || componentData.aiMediaType === 'video';
      const s = Math.min(width, height) * 0.14; // icon size scales with card

      if (isVideo) {
        // Play triangle icon
        icon.moveTo(-s * 0.45, -s * 0.55);
        icon.lineTo(s * 0.55, 0);
        icon.lineTo(-s * 0.45, s * 0.55);
        icon.closePath();
        icon.fill(0xc6e982, 0.7);
      } else {
        // Image icon: rounded rect with mountain/sun
        const iw = s * 1.4, ih = s * 1.1;
        // Frame
        icon.roundRect(-iw / 2, -ih / 2, iw, ih, s * 0.15);
        icon.stroke({ width: 1.8, color: 0xc6e982, alpha: 0.7 });
        // Sun circle
        icon.circle(-iw * 0.2, -ih * 0.15, s * 0.16);
        icon.fill(0xc6e982, 0.7);
        // Mountain shape
        icon.moveTo(-iw * 0.35, ih * 0.3);
        icon.lineTo(-iw * 0.05, -ih * 0.05);
        icon.lineTo(iw * 0.1, ih * 0.12);
        icon.lineTo(iw * 0.2, 0);
        icon.lineTo(iw * 0.38, ih * 0.3);
        icon.closePath();
        icon.fill(0xc6e982, 0.5);
      }
      placeholder.addChild(icon);

      // --- Gentle pulse animation ---
      let elapsed = 0;
      const animationFn = (ticker) => {
        if (placeholder.destroyed) return;
        elapsed += ticker.deltaTime / 60;
        // Smooth sine pulse on the icon opacity
        icon.alpha = 0.55 + Math.sin(elapsed * 2) * 0.25;
      };
      const pixiApp = useCanvasStore.getState().pixiApp;
      if (pixiApp) {
        pixiApp.ticker.add(animationFn);
      }
      placeholder._cleanupAnimation = () => {
        const app = useCanvasStore.getState().pixiApp;
        if (app) {
          app.ticker.remove(animationFn);
        }
      };

      // Set position
      placeholder.x = x;
      placeholder.y = y;
      placeholder.eventMode = 'static';
      placeholder.cursor = 'default';

      // Scale to reasonable size
      const maxSize = viewport.worldHeight / 1.2;
      const scale = Math.min(maxSize / width, maxSize / height);
      placeholder.scale.set(scale);

      // Store references
      placeholder.componentData = componentData;
      placeholder.elementId = elementId;
      placeholder.isAiSprite = true;

      // Create invisible interactive area
      const interactiveArea = new PIXI.Graphics();
      interactiveArea.rect(-width / 2, -height / 2, width, height);
      interactiveArea.fill(0x000000, 0);
      interactiveArea.x = x;
      interactiveArea.y = y;
      interactiveArea.eventMode = 'static';
      interactiveArea.cursor = 'default';
      interactiveArea.scale.set(scale);
      interactiveArea.elementId = elementId;

      placeholder.interactiveArea = interactiveArea;

      // Add drag functionality
      const handlePointerDown = (event) => {
        let targetSprite = placeholder;

        if (event.currentTarget === interactiveArea && interactiveArea.elementId) {
          const canvasElement = useCanvasStore.getState().canvasElements.find(el => el.id === interactiveArea.elementId);
          if (canvasElement && canvasElement.pixiSprite) {
            targetSprite = canvasElement.pixiSprite;
          }
        }

        if (!targetSprite || targetSprite.destroyed || targetSprite.scale === null) return;
        if (!targetSprite.parent) return;

        dragStart(targetSprite, event);
      };

      placeholder.on('pointerdown', handlePointerDown);
      interactiveArea.on('pointerdown', handlePointerDown);

      // Add hover handlers
      const handlePointerOver = () => {
        if (placeholder && placeholder.parent && placeholder.elementId) {
          setHoveredElement(placeholder.elementId);
        }
      };
      const handlePointerOut = () => setHoveredElement(null);

      placeholder.on('pointerover', handlePointerOver);
      placeholder.on('pointerout', handlePointerOut);
      interactiveArea.on('pointerover', handlePointerOver);
      interactiveArea.on('pointerout', handlePointerOut);

      return placeholder;
    } catch (error) {
      console.error('Error creating AI sprite:', error);
      return createPlaceholderSprite(
        componentData, componentId, x, y, viewport, dragStart,
        setHoveredElement, canvasElements, `AI sprite failed: ${error.message}`
      );
    }
  }, [dragStart, canvasElements, viewport, setHoveredElement, createImageSprite, createVideoSprite, createAudioSprite, createGifSprite]);

  return {
    createVideoSprite,
    createImageSprite,
    createAudioSprite,
    createTextSprite,
    createGifSprite,
    createAiSprite,
  };
};